import { ArrowForward } from "@mui/icons-material";
import { Box, Button, InputBase, Tooltip, CircularProgress, Typography, useTheme } from "@mui/material";
import { UUID } from "crypto";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AutoSizer } from "react-virtualized";

import { scrollQuestionIntoView } from "../../../utils/scrollQuestionIntoView";
import { useDeepChatSocketContext } from "../../../contexts/DeepChatSocketContext";
import EUComplianceText from "../../common/EUComplianceText";
import { BodyPanel, MainPanel, RightPanel } from "../Layout/Panels";
import StudyHeader from "../Layout/StudyHeader";
import useMarketResearchStore from "../useMarketResearchStore";
import useStudyMetadataStore from "../useStudyMetadataStore";
import ChatAnswerNode from "./ChatAnswerNode";
import ChatSessionOutline from "./ChatSessionOutline";
import ChangeStudyButton from "../ChangeStudyButton";
import { StudyIcon } from "../AllStudies/StudyIcon";

export const isValidQuestion = (question: string) => {
  const text = String(question);
  const words = text.split(" ");
  return text.length > 8 && words.length > 2 && words.some((_word) => _word.length > 2);
};

const getThreadId = (value: string) => {
  return value ? (value as UUID) : undefined;
};

export default function StudyChat() {
  const location = useLocation();
  const navigate = useNavigate();
  const DeepChatSocket = useDeepChatSocketContext();
  const theme = useTheme();

  const {
    scope,
    messages,
    question,
    invocationType,
    setQuestion,
    askQuestion,
    studyIds,
    clearChatSession,
    isLoadingChat,
    socketStatusMessage,
    numOfResponses,
    resetSocket,
    thinkingSteps,
    thinkingStepsCollapsed,
    setThinkingStepsCollapsed,
  } = useMarketResearchStore();
  const prevMessagesLengthRef = useRef(messages.length);

  // Legacy spinner only shows if we are loading but the backend hasn't sent steps yet
  const showLegacySpinner = isLoadingChat && thinkingSteps.length === 0;

  const startNewThread = () => {
    resetSocket();
    clearChatSession();
    navigate(-1);
  };

  // TODO: This needs to be an array
  const handleAskQuestion = (question: string, study_keys?: string[]) => {
    if (question.length && !isLoadingChat) {
      askQuestion(DeepChatSocket.sendJsonMessage, question, study_keys ?? []);
      setQuestion("");
    }
  };

  // Sync the session key once we get it from the current chat interaction
  // This may be overkill for trying to inject the UUID into the URI on page transition to this view
  useEffect(() => {
    const message = messages.at(-1);
    const path = location.pathname.split("/");
    const threadId = getThreadId(path.at(-1) ?? "");

    if (message && threadId != message.user_chat_session) {
      navigate(`/market-research/threads/${message.user_chat_session}`, {
        replace: true,
        state: { prevent_load: true },
      });
    }
  }, [messages, location]);

  // Scroll to the latest question when a new question is added to the session
  useEffect(() => {
    const currentLength = messages.length;

    if (currentLength > prevMessagesLengthRef.current) {
      const target = messages.at(-1);
      if (target) {
        const anchorId = `${target?.user_chat_session}-${currentLength - 1}`;
        scrollQuestionIntoView(anchorId);
      }
    }

    prevMessagesLengthRef.current = currentLength;
  }, [messages]);

  // Try to get study details by its "study_key"
  const studyId = studyIds.at(0);
  const { study } = useStudyMetadataStore(studyId as UUID);
  const isAllStudies = studyIds.length > 0;

  return (
    <MainPanel>
      <BodyPanel>
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%", paddingBottom: "32px", padding: "2px" }}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "16px" }}
            flexDirection={"row"}
          >
            {isAllStudies ? (
              <Box sx={{ display: "flex", gap: "5px", alignItems: "center" }}>
                <StudyIcon therapeuticArea="Not Applicable" size={32} />
                <Typography variant="h5" sx={{ color: "#004677" }}>
                  All Studies
                </Typography>
              </Box>
            ) : (
              <StudyHeader study={study} />
            )}
            {scope == "chat" && <ChangeStudyButton />}
          </Box>

          {messages.map((interaction, i) => {
            const anchorId = `${String(interaction.user_chat_session)}-${i}`;
            const isLastItem = messages.at(-1) === interaction;

            // Logic: Use global "live" steps for the active query,
            // otherwise use the saved history inside the message object.
            const stepsToDisplay =
              isLastItem && thinkingSteps.length > 0 ? thinkingSteps : (interaction.thinkingSteps ?? []);

            return (
              <ChatAnswerNode
                key={i}
                anchorId={anchorId}
                interaction={interaction}
                isLoading={isLastItem && isLoadingChat}
                loadingStatus={socketStatusMessage}
                numOfResponses={numOfResponses}
                thinkingSteps={stepsToDisplay}
                thinkingStepsCollapsed={isLastItem ? thinkingStepsCollapsed : true}
                onToggleThinkingStepsCollapse={() => {
                  if (isLastItem) setThinkingStepsCollapsed(!thinkingStepsCollapsed);
                }}
              />
            );
          })}

          {showLegacySpinner && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2, ml: "64px" }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>

        {/* Followup Question Input */}
        <Box sx={{ height: "84px" }} className="followup-input-base">
          <AutoSizer disableHeight>
            {({ width }) => (
              <Box
                sx={{
                  position: "fixed",
                  width: width,
                  bottom: 0,
                  backgroundImage: `linear-gradient(to bottom, rgba(245,245,249,0) 0%, ${theme.palette.background.default} 25%)`,
                  py: "2em",
                  // border: "1px solid blue",
                }}
              >
                <Box
                  sx={{
                    p: "10px 16px",
                    display: invocationType == "qna" ? "none" : "flex",
                    alignItems: "center",
                    backgroundColor: "white",
                    borderRadius: "32px",
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.05), 0 1px 4px rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <Tooltip
                    title={
                      question && !isValidQuestion(question)
                        ? "Please enter a valid question with 3 or more words"
                        : undefined
                    }
                  >
                    <InputBase
                      sx={{ width: "100%", ml: 1 }}
                      multiline
                      value={question}
                      placeholder="Ask a related follow-up question…"
                      disabled={isLoadingChat}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && !event.shiftKey && isValidQuestion(question)) {
                          event.preventDefault();
                          handleAskQuestion(question, studyIds);
                        }
                      }}
                      onChange={(event) => setQuestion(event.target.value)}
                    />
                  </Tooltip>
                  <Button
                    disableElevation
                    variant="contained"
                    aria-label="search"
                    disabled={!question || !isValidQuestion(question) || isLoadingChat}
                    onClick={() => handleAskQuestion(question, studyIds)}
                    sx={{
                      padding: "8px",
                      margin: "4px",
                      minWidth: "unset",
                      borderRadius: "50%",
                      backgroundColor: question.length === 0 ? "#E0E0E0" : "#023088",
                      color: question.length === 0 ? "#E0E0E0" : "white",
                    }}
                  >
                    <ArrowForward />
                  </Button>
                </Box>
                <EUComplianceText />
              </Box>
            )}
          </AutoSizer>
        </Box>
      </BodyPanel>

      <RightPanel>
        <ChatSessionOutline
          messages={messages}
          onClickNewThread={() => startNewThread()}
          invocationType={invocationType}
          onClickBackToStudy={() => navigate(-1)}
        />
      </RightPanel>
    </MainPanel>
  );
}
